import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useTeam } from "@/hooks/queries/useTeam";
import { useOrganizationSearchProfiles } from "@/hooks/queries/useOrganizationSearchProfiles";
import { useSetSearchProfileAssignees } from "@/hooks/mutations/useTeamMutations";
import { MemberAvatar } from "@/components/team/AssigneeSelect";
import type { OrganizationSearchProfile, TeamMember } from "@/types/domain";

function roleLabel(role: string) {
  if (role.includes("owner")) return "Titular";
  if (role.includes("admin")) return "Administrador";
  return "Comercial";
}

/**
 * La matriz persona × línea de negocio. Es el corazón del módulo: una línea sin
 * nadie marcado no la ve ni la recibe nadie, y eso no da error — da lista vacía.
 * Verlo como tabla es lo que hace visible ese hueco antes de que cueste caro.
 */
function AssignmentMatrix({
  members,
  profiles,
}: {
  members: TeamMember[];
  profiles: OrganizationSearchProfile[];
}) {
  const setAssignees = useSetSearchProfileAssignees();

  function toggle(profile: OrganizationSearchProfile, userId: string) {
    const next = new Set(profile.assignedUsers.map((user) => user.id));
    if (next.has(userId)) next.delete(userId);
    else next.add(userId);

    setAssignees.mutate(
      { searchprofile_id: profile.searchprofile_id, user_ids: [...next] },
      { onError: () => toast.error("No se pudo cambiar la asignación.") },
    );
  }

  const linesByUser = new Map<string, number>();
  for (const profile of profiles) {
    for (const user of profile.assignedUsers) {
      linesByUser.set(user.id, (linesByUser.get(user.id) ?? 0) + 1);
    }
  }

  const uncovered = profiles.filter((p) => p.assignedUsers.length === 0);

  return (
    <div className="flex flex-col gap-4">
      <p className="max-w-xl text-sm text-slate-500 dark:text-slate-400">
        Marca quién trabaja cada línea. Quien esté asignado ve sus oportunidades,
        recibe sus alertas y puede tomar las que no tienen responsable.
      </p>

      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full min-w-[640px] border-collapse text-left">
          <thead>
            <tr className="border-b bg-slate-50/70 dark:bg-slate-900/30">
              <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-widest text-slate-400">
                Persona
              </th>
              {profiles.map((profile) => (
                <th
                  key={profile.searchprofile_id}
                  className="border-l px-3 py-3 text-center align-top"
                >
                  <div className="truncate text-[13px] font-semibold text-slate-700 dark:text-slate-200">
                    {profile.searchprofile_name}
                  </div>
                  <div className="text-[11px] font-normal text-slate-400">
                    {profile.contractingCount ?? 0} licitación
                    {(profile.contractingCount ?? 0) === 1 ? "" : "es"}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {members.map((member) => {
              const lines = linesByUser.get(member.userId) ?? 0;
              return (
                <tr
                  key={member.userId}
                  className="border-b last:border-0 hover:bg-slate-50/60 dark:hover:bg-slate-900/20"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <MemberAvatar
                        member={{
                          id: member.userId,
                          name: member.name,
                          image: member.image,
                        }}
                        className="size-9"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                          {member.name}
                        </p>
                        <p className="flex items-center gap-2">
                          <Badge
                            variant="secondary"
                            className="px-1.5 py-0 text-[9px] uppercase tracking-wider"
                          >
                            {roleLabel(member.role)}
                          </Badge>
                          <span className="truncate text-[11px] text-slate-400">
                            {member.email}
                          </span>
                        </p>
                      </div>
                      <span className="shrink-0 text-right text-[11px] text-slate-400">
                        <span className="block text-base font-bold text-slate-700 tabular-nums dark:text-slate-200">
                          {lines}
                        </span>
                        línea{lines === 1 ? "" : "s"}
                      </span>
                    </div>
                  </td>

                  {profiles.map((profile) => {
                    const checked = profile.assignedUsers.some(
                      (user) => user.id === member.userId,
                    );
                    return (
                      <td
                        key={profile.searchprofile_id}
                        className={cn(
                          "border-l px-3 py-3 text-center",
                          checked && "bg-primary/5",
                        )}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={setAssignees.isPending}
                          onCheckedChange={() => toggle(profile, member.userId)}
                          aria-label={`${member.name} en ${profile.searchprofile_name}`}
                          className="cursor-pointer"
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr className="border-t bg-slate-50/70 dark:bg-slate-900/30">
              <td className="px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest text-slate-400">
                Cobertura
              </td>
              {profiles.map((profile) => {
                const count = profile.assignedUsers.length;
                return (
                  <td
                    key={profile.searchprofile_id}
                    className="border-l px-3 py-2.5 text-center text-[12px] font-semibold"
                  >
                    {count === 0 ? (
                      <span className="text-red-500">nadie</span>
                    ) : (
                      <span className="text-slate-600 dark:text-slate-300">
                        {count} persona{count === 1 ? "" : "s"}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>

      {uncovered.length > 0 && (
        <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/50 dark:bg-amber-950/20">
          <span className="mt-1.5 size-2 shrink-0 rounded-full bg-amber-500" />
          <div>
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {uncovered.length === 1
                ? `La línea ${uncovered[0]!.searchprofile_name} no tiene a nadie asignado`
                : `${uncovered.length} líneas no tienen a nadie asignado`}
            </p>
            <p className="text-[13px] text-amber-700 dark:text-amber-300/80">
              Nadie ve esas oportunidades ni recibe sus alertas.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Administración del equipo: quién trabaja cada línea de negocio. Vive en un
 * diálogo porque se toca una vez al mes y no debe competir con el pipeline, que es
 * el trabajo diario.
 */
export function TeamManagement({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: team } = useTeam();
  const canManage = !!team?.canManageSearchProfiles;
  const { data: profiles } = useOrganizationSearchProfiles(canManage && open);

  if (!team?.isTeam || !canManage) return null;

  const activeMembers = team.members.filter((member) => member.isActive);
  const uncovered =
    profiles?.filter((p) => p.assignedUsers.length === 0).length ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">Administrar equipo</DialogTitle>
          <DialogDescription>
            {activeMembers.length} persona{activeMembers.length === 1 ? "" : "s"} ·{" "}
            {profiles?.length ?? 0} línea{profiles?.length === 1 ? "" : "s"} de
            negocio
            {uncovered > 0 && (
              <span className="text-amber-600 dark:text-amber-400">
                {" · "}
                {uncovered} sin cubrir
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {profiles?.length ? (
          <AssignmentMatrix members={activeMembers} profiles={profiles} />
        ) : (
          <p className="py-10 text-center text-sm text-slate-400">
            Todavía no hay líneas de negocio que asignar.
          </p>
        )}

      </DialogContent>
    </Dialog>
  );
}
